import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import { APP_TITLE } from "../../../config/constant";
import { defaultStatusColors } from "../../../utils/CardUtils";
import * as Library from "../../../utils/Library";
import InfinityGrid from "../../common/cardContainers/InfinityGrid";
import BuyingIntelGridCard from "../../common/gridCards/BuyingIntelGridCard";
import ListComponent from "../../common/lists/ListComponent";
import MainWindowHeader, {
    CustomSearch,
    OrganizeBtnMenu
} from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import { BUYING_INTEL_PAGE_TYPES, DEFAULT_BUYING_CYCLE_ID } from "./buyingIntelPageConfig";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.hideOverflow {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }

    &.lessMarginTop {
        margin-top: -24px;
    }
`;
// #endregion

// #region CONFIG
const BUYING_INTEL_API_CONFIG = Object.freeze({
    endpointName: "getBuyerReportsData"
});
// #endregion

export default function BIPage() {
    document.title = `${APP_TITLE} - Buying Intelligence`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();
    const navigations = useSelector(state => state.navigationAccesses);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const gridRef = useRef(null);
    const currentCompanyID = localStorage.getItem("currentCompanyID");
    // #endregion

    // #region STATES
    const [listNumRows, setListNumRows] = useState(50);
    const [totalItems, setTotalItems] = useState(0);
    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");

    //---------- Header Filters
    const [allConnectedSeasons, setAllConnectedSeasons] = useState([]);
    const [allSeasonsList, setAllSeasonsList] = useState([]);
    const [filteredSeasons, setFilteredSeasons] = useState([]);
    //-----
    const [allLabelBrandsList, setAllLabelBrandsList] = useState([]);
    const [filteredLabelBrand, setFilteredLabelBrand] = useState([]);
    //-----
    const [requestEnabled, setRequestsEnabled] = useState(false);

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties
    };

    const filters = {
        labelBrandIDs: filteredLabelBrand?.map(item => item?.value),
        seasonNames: filteredSeasons?.map(item => item?.name)
    };

    const infiniteGridQueryKey = ["BuyingIntelligenceGrid", currentCompanyID, filters];
    const listQueryKey = ["BuyingIntelligenceList", currentCompanyID, filters];

    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);

        setupHeaders(
            props,
            onClickRefreshAction,
            navigations,
            onSeasonChange,
            onLabelBrandChange,
            onFiltersLoaded,
            allLabelBrandsList,
            allSeasonsList
        );
    }, [props?.layoutType, navigations, allLabelBrandsList, allSeasonsList]);

    // #endregion

    // #region METHODS
    const onLabelBrandChange = (selections = [], dropdownDataWithSelections = []) => {
        const newSelections = selections
            .map(selection => {
                return { ...selection, selected: selection.locked ? false : selection.selected };
            })
            .filter(selection => !selection.locked);

        const newDropdownDataWithSelections = dropdownDataWithSelections.map(selection => {
            return { ...selection, selected: selection.locked ? false : selection.selected };
        });

        const isSame = JSON.stringify(newSelections) === JSON.stringify(filteredLabelBrand);
        if (isSame) return;

        const selectedLabelBrandIDs = newSelections.map(selection => selection?.value);

        setFilteredLabelBrand(newSelections);
        setAllLabelBrandsList(newDropdownDataWithSelections);
        filterConnectedSeasons(allConnectedSeasons, selectedLabelBrandIDs);
    };

    const onSeasonChange = (selections, dropdownDataWithSelections) => {
        setFilteredSeasons(selections);
        setAllSeasonsList(dropdownDataWithSelections);
    };

    const onFiltersLoaded = data => {
        let selectedLabelBrandIDs = [];

        if (data?.connectedLabelBrands?.length) {
            const labelBrands = data?.connectedLabelBrands?.map(item => {
                const defaultLabelBrandID = localStorage.getItem("defaultLabelBrand");
                let labelBrandName = item?.name;

                if (item?._id === defaultLabelBrandID) {
                    labelBrandName = `${item?.name} (default)`;
                }
                const restrictedLabelBrands = JSON.parse(localStorage.getItem("restrictedLabelBrandIDs") || "[]");
                const locked = restrictedLabelBrands.includes(item?._id);

                return {
                    name: labelBrandName,
                    value: item?._id,
                    selected: !locked,
                    locked: locked
                };
            });

            const selectedLabelBrands = labelBrands?.filter(item => item?.selected);

            setAllLabelBrandsList(labelBrands);
            setFilteredLabelBrand(selectedLabelBrands);
            selectedLabelBrandIDs = selectedLabelBrands.map(item => item?.value);
        }

        if (data?.connectedSeasons?.length) {
            const seasons = data?.connectedSeasons?.map(item => {
                return {
                    name: item?.name,
                    value: item?.name,
                    selected: true,
                    labelBrandID: item?.labelBrandID,
                    retailBrandID: item?.retailBrandID
                };
            });

            setAllConnectedSeasons(seasons);
            setFilteredSeasons(seasons?.filter(item => item?.selected));
            filterConnectedSeasons(seasons, selectedLabelBrandIDs);
        }

        setRequestsEnabled(true);
    };

    const filterConnectedSeasons = (allSeasons, selectedLabelBrandIDs) => {
        const labelBrandIDs = Array.isArray(selectedLabelBrandIDs)
            ? selectedLabelBrandIDs
            : [selectedLabelBrandIDs].filter(Boolean);

        let res = allSeasons?.filter(item => {
            return labelBrandIDs.includes(item?.labelBrandID);
        });

        // Filter out duplicated season names across the selected label brands.
        res = res?.filter((item, index) => {
            return res?.findIndex(item2 => item2?.value === item?.value) === index;
        });

        setAllSeasonsList(res);
        setFilteredSeasons(res);
    };

    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") queryClient.invalidateQueries(listQueryKey);
        else gridRef.current?.reset?.();
    };

    const onClickViewDashboard = item => {
        const buyingCycleID =
            item?.buyingCycleID ?? item?.ourID ?? item?.botagID ?? item?.productID ?? DEFAULT_BUYING_CYCLE_ID;
        navigate(`/buying-intel/overview/${encodeURIComponent(buyingCycleID)}`);
    };

    const openMainURL = url => {
        if (!url) {
            Library.showToastMessage({
                type: "warning",
                message: "PDF is not yet generated"
            });
            return;
        }

        window.open(url, "_blank");
    };
    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "BOtag ID",
            selector: row => row?.botagID ?? "--"
        },
        {
            name: "Brand",
            selector: row => row?.brand ?? "--"
        },
        {
            name: "Style name",
            selector: row => row?.styleName ?? "--"
        },
        {
            name: "Item nr.",
            selector: row => row?.reference ?? "--"
        },
        {
            name: "Colors",
            selector: row => row?.colorNames?.join(", ") ?? "--"
        },
        {
            name: "Sizes",
            selector: row => row?.sizes?.join(", ") ?? "--"
        },
        {
            name: "Category",
            selector: row => row?.category ?? "--"
        },
        {
            name: "My stock",
            selector: row =>
                `${row?.totalStockInStores || "0"} ${row?.lastStockCheck ? `( ${row?.lastStockCheck} )` : ""}`
        },
        {
            name: "Supplier stock",
            selector: row =>
                `${row?.supplierStock || "0"} ${
                    row?.lastSupplierStockCheck ? `( ${row?.lastSupplierStockCheck} )` : ""
                }`
        }
    ];
    // #endregion

    return (
        <StyledPage className={`lessMarginTop ${viewLayoutStyle === "Grid" ? "hideOverflow" : ""}`}>
            {viewLayoutStyle === "Grid" && (
                <InfinityGrid
                    ref={gridRef}
                    className="grid"
                    pageCard={BuyingIntelGridCard}
                    queryKey={infiniteGridQueryKey}
                    minColumnWidth="395px"
                    imageHeight="100px"
                    skeletonHeight="460px"
                    endpointName={BUYING_INTEL_API_CONFIG.endpointName}
                    payload={{
                        pageType: BUYING_INTEL_PAGE_TYPES.BUYING_CYCLES,
                        companyID: currentCompanyID,
                        limit: 30,
                        filters
                    }}
                    requestEnabled={requestEnabled}
                    getItemKey={item => item?.buyingCycleID ?? item?.ourID ?? item?.botagID ?? item?.productID}
                    showEmptyCardWithData={false}
                    thirdButtonOnClick={onClickViewDashboard}
                />
            )}

            {viewLayoutStyle === "List" && (
                <ListComponent
                    title="Products"
                    columns={listColumns}
                    api={{
                        endpoint: BUYING_INTEL_API_CONFIG.endpointName,
                        formData: {
                            pageType: BUYING_INTEL_PAGE_TYPES.BUYING_CYCLES,
                            companyID: currentCompanyID,
                            limit: listNumRows,
                            filters
                        },
                        onSuccess: data => {
                            if (!totalItems) setTotalItems(data?.totalCount || 0);
                            return data;
                        }
                    }}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    actions={{
                        editIsVisible: false,
                        includeActions: true,
                        suspendIsVisible: true,
                        removeIsVisible: false,
                        extraActions: [
                            {
                                title: "FILES",
                                onClick: item => openMainURL(item?.mainURL),
                                isDisabled: item => item?.status !== "Completed"
                                //onClick: onClickFiles
                            },
                            {
                                title: "NANABO"
                                //onClick: item => onClickNanoBO(item)
                            },
                            {
                                title: "VIEW DASHBOARD"
                                //onClick: onClickViewDashboard
                            }
                        ]
                    }}
                    includeStatus={{
                        stickyStatus: true,
                        statusFromPath: "status",
                        statusColors: defaultStatusColors
                    }}
                    requestEnabled={requestEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                />
            )}
        </StyledPage>
    );
}

// #region SMALL COMPONENTS
function setupHeaders(
    props,
    onRefreshAction,
    navigations,
    onSeasonChange,
    onLabelBrandChange,
    onFiltersLoaded,
    allLabelBrandsList,
    allSeasonsList
) {
    const createMode = navigations?.navigationAccesses?.buyingIntel?.createModeOnly;

    props.setControlBarLeftBtns([
        {
            customSearch: <CustomSearch enabled={false} placeholder="Brand, season recommendation or order" />
        }
    ]);
    props.setControlBarCenterBtns([
        { to: "/buying-intel", active: true, disabled: false, title: "BUYING CYCLES" },
        { to: "/buying-intel/portfolio", active: false, title: "PORTFOLIO", disabled: true },
        { to: "/buying-intel/actions", active: false, title: "ACTIONS", disabled: true }
    ]);
    props.setControlBarRightBtns([
        { to: "/buying-intel", active: true, disabled: createMode, title: "View", icon: "eye" },
        {
            to: "/buying-intel/add",
            active: false,
            disabled: false,
            title: "Add",
            icon: "plus"
        },
        {
            to: `/buying-intel/overview/${DEFAULT_BUYING_CYCLE_ID}`,
            active: false,
            disabled: true,
            title: "Overview",
            icon: "presentation-chart-01"
        },
        {
            to: `/buying-intel/buyplan/${DEFAULT_BUYING_CYCLE_ID}`,
            active: false,
            disabled: true,
            title: "Buy plan",
            icon: "lightbulb-05"
        },
        {
            to: `/buying-intel/order/${DEFAULT_BUYING_CYCLE_ID}`,
            active: false,
            disabled: true,
            title: "Order",
            icon: "scale-02"
        },
        {
            to: `/buying-intel/forecast/${DEFAULT_BUYING_CYCLE_ID}`,
            active: false,
            disabled: true,
            title: "Forecast",
            icon: "line-chart-up-01"
        }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: false
                }}
                search={{
                    visible: false
                }}
                onRefreshClick={onRefreshAction}
                firstOrganize={{
                    visible: true,
                    dropdownData: allLabelBrandsList,
                    onDropdownChange: onLabelBrandChange,
                    disabled: allLabelBrandsList?.length === 1,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all label brands",
                        labelAllSelected: "All label brands",
                        labelNoneSelected: "--",
                        allItems: allLabelBrandsList
                    }
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allSeasonsList,
                    onDropdownChange: onSeasonChange,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all seasons",
                        labelAllSelected: "All seasons",
                        labelNoneSelected: "--",
                        allItems: allSeasonsList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useConnectedLabelBrands: true,
                        useConnectedProductSeasons: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                settingsButton={{
                    visible: true,
                    enabled: false
                }}
            />
        )
    });
}

export const ServerSetupSettingsMenuComponent = () => {
    const navigate = useNavigate();

    return (
        <OrganizeBtnMenu
            items={[
                {
                    name: "Server Setup",
                    value: "serverSetup"
                }
            ]}
            onItemClick={() => navigate("/server")}
        />
    );
};
// #endregion
